import { readManagedImage, type MediaItem, type MediaTarget } from '@/api/core/property-media';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { LoadFailure } from '@/components/load-failure';
import { LoadMoreFooter } from '@/components/load-more-footer';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import { ZoomableImg } from '@/components/zoomable-img';
import { safeImageUri } from '@/lib/safe-image-uri';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@mgrvtechgroup/admin-ds';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { FileIcon, FilmIcon } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { invalidateHomepagePreview } from '../homepage-preview-cache';
import { propertyRead, type PropertyRuntime } from '../property-queries';
import { MediaEditForm } from './media-edit-form';
import {
  categoryLabel,
  ITEM_KIND_LABEL,
  languageLabel,
  OBSERVATION_STATE_LABEL,
  purposeLabel,
  SELECTION_ROLE_LABEL,
} from './media-presentation';
import { MEDIA_GROUPS_QUERY, propertyMediaItemsQuery } from './media-queries';
import { MediaRetirementForm, MediaRetirementRecovery } from './media-retirement-form';
import { MediaWriteForm, useMediaWriteOwner } from './media-write-form';

function PhotoAddForm({
  open,
  onResume,
  onClose,
  ...props
}: Parameters<typeof MediaWriteForm>[0] & {
  open: boolean;
  onResume: () => void;
  onClose: () => void;
}) {
  const owner = useMediaWriteOwner();
  const pendingCollection =
    owner.attempt?.kind === 'file' ? owner.attempt.value.collectionId : owner.attempt?.collectionId;
  const pendingHere = pendingCollection === props.collectionId;
  useEffect(() => {
    if (pendingHere) queueMicrotask(onResume);
  }, [pendingHere, onResume]);
  const { onSaved } = props;
  // 등록 결과가 불명확한 요청이 남아 있으면 닫지 못하게 해 같은 요청 재시도 길을 유지한다.
  return (
    <Dialog
      open={open || pendingHere}
      onOpenChange={(next) => {
        if (!next && !pendingHere) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>사진 추가</DialogTitle>
          <DialogDescription>
            파일을 올리거나 HTTPS 주소를 입력해 이 묶음에 사진을 등록합니다.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          <MediaWriteForm
            {...props}
            onSaved={() => {
              onClose();
              onSaved();
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

const SUBJECT = '사진·영상 항목';

type ImageRead = (read: () => Promise<Blob>, signal: AbortSignal) => Promise<Blob>;
const ImageReadContext = createContext<ImageRead>((read) => read());
function imageReadQueue(): ImageRead {
  let active = 0;
  const waiting: (() => void)[] = [];
  return (read, signal) =>
    new Promise((resolve, reject) => {
      const start = () => {
        if (signal.aborted) {
          reject(new DOMException('Aborted', 'AbortError'));
          waiting.shift()?.();
          return;
        }
        active++;
        void read()
          .then(resolve, reject)
          .finally(() => {
            active--;
            waiting.shift()?.();
          });
      };
      if (active < 3) start();
      else waiting.push(start);
    });
}

function ManagedPreview({
  readEnabled,
  item,
  label,
  runtime,
  generation,
  isCurrent,
}: {
  readEnabled: boolean;
  item: MediaItem;
  label: string;
  runtime: PropertyRuntime;
  generation: number;
  isCurrent: () => boolean;
}) {
  const session = useOperatorSession(runtime.session);
  const enqueueRead = useContext(ImageReadContext);
  const [requested, setRequested] = useState(false);
  const [retry, setRetry] = useState(0);
  const previewRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = previewRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      queueMicrotask(() => {
        setRequested(true);
      });
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRequested(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);
  const [state, setState] = useState<{
    item: MediaItem | null;
    url: string | null;
    error: boolean;
  }>({
    item: null,
    url: null,
    error: false,
  });
  const authorized =
    readEnabled &&
    session.status === 'authorized' &&
    session.generation === generation &&
    isCurrent();
  useEffect(() => {
    if (!authorized || !requested) return;
    const controller = new AbortController();
    let active = true;
    let createdUrl: string | null = null;
    void enqueueRead(
      () =>
        propertyRead(
          runtime,
          generation,
          () => readManagedImage(runtime.client, item, controller.signal),
          controller.signal,
          isCurrent,
        ),
      controller.signal,
    )
      .then((blob) => {
        const current = runtime.session.getSnapshot();
        if (
          !active ||
          controller.signal.aborted ||
          current.status !== 'authorized' ||
          current.generation !== generation ||
          !isCurrent()
        )
          return;
        createdUrl = URL.createObjectURL(blob);
        setState({ item, url: createdUrl, error: false });
      })
      .catch(() => {
        if (active && !controller.signal.aborted) setState({ item, url: null, error: true });
      });
    return () => {
      active = false;
      controller.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [authorized, generation, isCurrent, item, requested, runtime, enqueueRead, retry]);
  const box =
    'bg-muted/40 text-muted-foreground flex aspect-video min-h-20 w-full min-w-0 items-center justify-center overflow-hidden rounded-md border p-1 text-center sm:p-2';
  const visible = state.item === item ? state : null;
  if (!authorized) return null;
  if (!requested)
    return (
      <div ref={previewRef} className={box} role="status">
        사진을 불러올 준비가 되었습니다.
      </div>
    );
  if (visible?.error)
    return (
      <div className={box} role="status">
        <div>
          <p className="caption">사진을 표시할 수 없습니다</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setState({ item: null, url: null, error: false });
              setRetry((value) => value + 1);
            }}
          >
            사진 다시 불러오기
          </Button>
        </div>
      </div>
    );
  if (!visible?.url)
    return (
      <div className={box} role="status">
        사진을 불러오고 있습니다
      </div>
    );
  return (
    <div className={box}>
      <ZoomableImg
        buttonClassName="size-full"
        src={visible.url}
        zoomSrc={visible.url}
        alt={label}
        onError={() => {
          if (visible.url) URL.revokeObjectURL(visible.url);
          setState({ item, url: null, error: true });
        }}
        className="size-full object-contain"
      />
    </div>
  );
}

function uniqueById(items: MediaItem[]): MediaItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function Preview({
  readEnabled,
  item,
  label,
  failed,
  onError,
  runtime,
  generation,
  isCurrent,
}: {
  readEnabled: boolean;
  item: MediaItem;
  label: string;
  failed: boolean;
  onError: () => void;
  runtime: PropertyRuntime;
  generation: number;
  isCurrent: () => boolean;
}) {
  if (item.codecVersion === 3)
    return (
      <ManagedPreview
        readEnabled={readEnabled}
        item={item}
        label={label}
        runtime={runtime}
        generation={generation}
        isCurrent={isCurrent}
      />
    );
  const box =
    'bg-muted/40 text-muted-foreground flex aspect-video min-h-20 w-full min-w-0 items-center justify-center overflow-hidden rounded-md border p-1 text-center sm:p-2';
  if (item.kind !== 'IMAGE')
    return (
      <div className={box}>
        <span className="caption flex items-center gap-1.5">
          {item.kind === 'VIDEO' ? (
            <FilmIcon aria-hidden className="size-4" />
          ) : (
            <FileIcon aria-hidden className="size-4" />
          )}
          {ITEM_KIND_LABEL[item.kind]} 미리보기는 제공하지 않습니다
        </span>
      </div>
    );
  // 작은 썸네일이 있으면 그것을, 없으면 원본 주소를 쓴다. 안전하지 않은 주소는 이미지로 올리지 않는다.
  const uri = safeImageUri(item.thumbnailUri) ?? safeImageUri(item.primaryUri);
  if (!readEnabled) return <div className={box} />;
  if (!uri)
    return (
      <div className={box}>
        <span role="status" className="caption">
          표시할 수 없는 주소입니다
        </span>
      </div>
    );
  if (failed)
    return (
      <div className={box}>
        <span role="status" className="caption">
          사진을 표시할 수 없습니다
        </span>
      </div>
    );
  // 확대할 때는 작은 썸네일 대신 원본을 보여 준다.
  const full = safeImageUri(item.primaryUri) ?? uri;
  return (
    <div className={box}>
      <ZoomableImg
        buttonClassName="size-full"
        src={uri}
        zoomSrc={full}
        alt={label}
        loading="lazy"
        decoding="async"
        onError={onError}
        className="size-full object-contain"
      />
    </div>
  );
}

/**
 * 선택한 그룹의 항목을 다이얼로그로 보여 준다. 항목과 항목별 "저장된 공개 선택"을 서버가 독립 페이지로 준다 —
 * 공개 적격성은 서버도 계산하지 않으므로 여기서도 판정하지 않는다.
 */
export function MediaItemsPanel({
  readEnabled = true,
  runtime,
  generation,
  propertyId,
  groupId,
  isCurrent,
  scopeLabel,
  onClose,
  expectedTarget,
  allowNativeWrite = false,
  nativeCurrent = false,
  inline = false,
  adding = false,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  groupId: string;
  isCurrent: () => boolean;
  /** 이 그룹이 속한 대상 이름 — 서버가 그룹에서 정한 scope를 풀어 쓴 것 */
  scopeLabel: (scope: { kind: string; id: string | null; floor: number | null }) => string;
  onClose: () => void;
  expectedTarget?: MediaTarget;
  allowNativeWrite?: boolean;
  nativeCurrent?: boolean;
  inline?: boolean;
  adding?: boolean;
}) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(adding);
  const resumeAdd = useCallback(() => {
    setAddOpen(true);
  }, []);
  useEffect(() => {
    if (adding)
      queueMicrotask(() => {
        setAddOpen(true);
      });
  }, [adding]);

  const readImage = useMemo(() => imageReadQueue(), []);
  const options = propertyMediaItemsQuery(
    runtime,
    generation,
    propertyId,
    groupId,
    readEnabled,
    isCurrent,
    nativeCurrent,
  );
  const query = useInfiniteQuery(options);
  const [failedImages, setFailedImages] = useState<ReadonlySet<string>>(() => new Set());
  const scopeKey = JSON.stringify([generation, propertyId, groupId, expectedTarget]);
  const [receipt, setReceipt] = useState<{ scope: string; message: string } | null>(null);
  const refreshButton = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  const pages = query.data?.pages;
  useEffect(() => {
    if (restoreFocus.current && pages && !query.isFetching) {
      refreshButton.current?.focus();
      restoreFocus.current = false;
    }
  }, [pages, query.isFetching]);
  const latest = pages?.at(-1);
  const items = useMemo(() => uniqueById(pages?.flatMap((page) => page.items) ?? []), [pages]);
  const restart = () => {
    setFailedImages(new Set());
    void invalidateHomepagePreview(queryClient);
    void queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
  };
  const shell = (title: string, description: string | undefined, body: React.ReactNode) =>
    inline ? (
      <section aria-label="등록된 사진" className="min-w-0 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">현재 사진</h3>
          <Button
            ref={refreshButton}
            variant="outline"
            onClick={() => {
              setReceipt(null);
              restart();
            }}
          >
            사진 목록 새로고침
          </Button>
        </div>
        {receipt?.scope === scopeKey && <p role="status">{receipt.message}</p>}
        <ImageReadContext.Provider value={readImage}>{body}</ImageReadContext.Provider>
      </section>
    ) : (
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          <DialogBody className="flex max-h-[70vh] min-w-0 flex-col gap-4 overflow-y-auto">
            {receipt?.scope === scopeKey && (
              <p role="status" aria-live="polite" className="break-all">
                {receipt.message}
              </p>
            )}
            <Button
              ref={refreshButton}
              variant="outline"
              onClick={() => {
                setReceipt(null);
                restart();
              }}
            >
              사진 목록 새로고침
            </Button>
            <ImageReadContext.Provider value={readImage}>{body}</ImageReadContext.Provider>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );

  if (query.isError && !pages)
    return shell(
      '사진·영상 항목',
      undefined,
      <LoadFailure error={query.error} subject={SUBJECT} onRetry={() => void query.refetch()} />,
    );
  if (!pages || !latest)
    return shell(
      '사진·영상 항목',
      undefined,
      <>
        <LoadingAnnouncement>사진·영상 항목을 불러오고 있습니다.</LoadingAnnouncement>
        <div aria-hidden className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="aspect-video w-full" />
          ))}
        </div>
      </>,
    );

  if (
    expectedTarget &&
    pages.some(
      (page) =>
        page.scope.propertyId.toLowerCase() !== propertyId.toLowerCase() ||
        page.group.id.toLowerCase() !== groupId.toLowerCase() ||
        page.scope.targetKind !== expectedTarget.kind ||
        page.scope.targetId?.toLowerCase() !== expectedTarget.id?.toLowerCase() ||
        page.scope.floor !== expectedTarget.floor,
    )
  )
    return shell(
      '그룹 항목',
      undefined,
      <p role="alert">사진 그룹의 대상을 확인할 수 없습니다.</p>,
    );

  const { group } = latest;
  const heading = `${purposeLabel(group.purpose)} · ${languageLabel(group.language)}`;
  const nextError = query.isFetchNextPageError ? query.error : null;
  const cursorLost =
    isApiError(nextError) && (nextError.status === 409 || nextError.status === 422);
  return shell(
    `사진·영상 항목 · ${heading}`,
    `분류 ${categoryLabel(group.categoryName)} · 대상 ${scopeLabel({ kind: latest.scope.targetKind, id: latest.scope.targetId, floor: latest.scope.floor })}`,
    <>
      {allowNativeWrite ? (
        <div className="rounded-md border p-3 empty:hidden">
          {!inline && (
            <Button
              variant="outline"
              onClick={() => {
                setAddOpen(!addOpen);
              }}
            >
              사진 추가
            </Button>
          )}
          <PhotoAddForm
            open={addOpen}
            onResume={resumeAdd}
            onClose={() => {
              setAddOpen(false);
            }}
            runtime={runtime}
            generation={generation}
            propertyId={propertyId}
            target={
              expectedTarget ?? {
                kind: latest.scope.targetKind,
                id: latest.scope.targetId,
                floor: latest.scope.floor,
              }
            }
            collectionId={groupId}
            revision={group.collectionRevision}
            onSaved={restart}
          />
        </div>
      ) : null}
      {query.isRefetchError ? (
        <LoadFailure error={query.error} subject={SUBJECT} onRetry={restart} />
      ) : null}
      {allowNativeWrite && nativeCurrent && expectedTarget && (
        <MediaRetirementRecovery
          runtime={runtime}
          generation={generation}
          propertyId={propertyId}
          target={expectedTarget}
          isCurrent={isCurrent}
          visibleItems={{ collectionId: groupId, items }}
          onSaved={async () => {
            setReceipt({ scope: scopeKey, message: '사진 삭제 결과를 확인했습니다.' });
            restoreFocus.current = true;
            void invalidateHomepagePreview(queryClient);
            await queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
          }}
        />
      )}
      {allowNativeWrite &&
        nativeCurrent &&
        expectedTarget &&
        items.length === 0 &&
        !query.hasNextPage &&
        !query.isError && (
          <MediaRetirementForm
            runtime={runtime}
            generation={generation}
            propertyId={propertyId}
            target={expectedTarget}
            collectionId={groupId}
            isCurrent={isCurrent}
            onSaved={async () => {
              void invalidateHomepagePreview(queryClient);
              await queryClient.resetQueries({
                predicate: (q) =>
                  q.queryKey.includes(MEDIA_GROUPS_QUERY) &&
                  q.queryKey.includes(propertyId) &&
                  q.queryKey.includes(generation),
              });
              onClose();
            }}
          />
        )}
      {items.length === 0 ? (
        <p className="body text-muted-foreground">이 묶음에 저장된 항목이 없습니다.</p>
      ) : (
        <ul
          aria-label="사진·영상 항목"
          className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {items.map((item) => {
            const failKey = `${item.id}\u0000${item.primaryUri ?? ''}`;
            return (
              <li
                key={item.id}
                className="grid min-w-0 grid-cols-[8rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 rounded-md border p-3 sm:flex sm:flex-col"
              >
                {/* 미리보기가 없을 때도 첫 칸을 지켜 상태 글이 좁은 칸으로 밀려 들어가지 않게 하고, 칸보다 넓어지지 않게 한다. */}
                <div className="min-w-0">
                  <Preview
                    readEnabled={readEnabled}
                    item={item}
                    runtime={runtime}
                    generation={generation}
                    isCurrent={isCurrent}
                    label={`${heading} ${String(item.sourceOrder === null ? '' : item.sourceOrder + 1)}`.trim()}
                    failed={failedImages.has(failKey)}
                    onError={() => {
                      setFailedImages((current) => new Set(current).add(failKey));
                    }}
                  />
                </div>
                {/* 좁은 화면에서는 썸네일 옆에 순서·사용 상태를 둬서 카드 한 장이 화면을 다 차지하지 않게 한다. */}
                <div className="flex min-w-0 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {item.kind !== 'IMAGE' && (
                      <Badge variant="outline">{ITEM_KIND_LABEL[item.kind]}</Badge>
                    )}
                    <span className="caption text-muted-foreground tabular-nums">
                      순서 {item.sourceOrder === null ? '없음' : item.sourceOrder + 1}
                    </span>
                    {item.contentSlot === null ? null : (
                      <span className="caption text-muted-foreground break-all">
                        위치 {item.contentSlot}
                      </span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="caption text-muted-foreground">
                      {item.savedPublicSelections.length
                        ? latest.publication?.status === 'PUBLISHED'
                          ? '웹사이트 사용 설정 · 노출 미확인'
                          : latest.publication?.status === 'UNPUBLISHED'
                            ? '선택됨 · 숨김 설정'
                            : '선택됨 · 노출 미확인'
                        : '보관 중'}
                    </p>
                    {item.savedPublicSelections.length === 0 ? (
                      <p className="caption">선택되지 않음</p>
                    ) : (
                      <ul aria-label="저장된 공개 선택" className="flex flex-wrap gap-1">
                        {item.savedPublicSelections.map((selection) => (
                          <li key={selection.id}>
                            <Badge variant="outline" tone="info">
                              {SELECTION_ROLE_LABEL[selection.role]} ·{' '}
                              {String(selection.groupOrder + 1)}-{String(selection.imageOrder + 1)}
                              {selection.language === 'und'
                                ? ''
                                : ` · ${languageLabel(selection.language)}`}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="col-span-2 flex min-w-0 flex-col gap-2 sm:col-span-1">
                  {allowNativeWrite && expectedTarget && item.kind === 'IMAGE' && (
                    <MediaEditForm
                      runtime={runtime}
                      generation={generation}
                      propertyId={propertyId}
                      target={expectedTarget}
                      collectionId={groupId}
                      itemId={item.id}
                      initialSelection={{
                        rootAbsent:
                          latest.publicationRecordState === 'ABSENT' && latest.publication === null,
                        category: group.categoryName,
                        language: group.language,
                        selectionSupported: group.purpose !== 'COMMON_AREA',
                      }}
                      isCurrent={isCurrent}
                      originalPreview={
                        <Preview
                          readEnabled={readEnabled}
                          item={item}
                          runtime={runtime}
                          generation={generation}
                          isCurrent={isCurrent}
                          label="원본 사진 미리보기"
                          failed={failedImages.has(failKey)}
                          onError={() => {
                            setFailedImages((current) => new Set(current).add(failKey));
                          }}
                        />
                      }
                      onAction={() => {
                        setReceipt(null);
                      }}
                      onSaved={(message) => {
                        setReceipt({ scope: scopeKey, message });
                        restoreFocus.current = true;
                        restart();
                      }}
                    />
                  )}
                  {allowNativeWrite && nativeCurrent && expectedTarget && item.kind === 'IMAGE' && (
                    <MediaRetirementForm
                      runtime={runtime}
                      generation={generation}
                      propertyId={propertyId}
                      target={expectedTarget}
                      collectionId={groupId}
                      itemId={item.id}
                      isCurrent={isCurrent}
                      onSaved={async () => {
                        setReceipt({ scope: scopeKey, message: '사진 삭제 결과를 확인했습니다.' });
                        restoreFocus.current = true;
                        void invalidateHomepagePreview(queryClient);
                        await queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
                      }}
                    />
                  )}
                  <details className="caption">
                    <summary className="text-muted-foreground cursor-pointer">등록 정보</summary>
                    <p className="break-all select-text">모음 ID {groupId}</p>
                    <p className="break-all select-text">항목 ID {item.id}</p>
                    {item.observationState !== null && (
                      <p>기록 상태 {OBSERVATION_STATE_LABEL[item.observationState]}</p>
                    )}
                    <p className="mt-1 break-all select-text">
                      {item.codecVersion === 3
                        ? '관리 사진 · 보호된 주소'
                        : `등록한 사진 주소 ${item.primaryUri ?? '주소 미설정'}`}
                    </p>
                    {item.thumbnailUri === null ? null : (
                      <p className="mt-1 break-all select-text">
                        작은 사진 주소 {item.thumbnailUri}
                      </p>
                    )}
                  </details>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {items.length > 0 || query.hasNextPage ? (
        <LoadMoreFooter
          label="사진·영상 항목"
          unit="개 항목"
          loaded={items.length}
          total={latest.page.total}
          hasMore={query.hasNextPage}
          isFetching={query.isFetchingNextPage}
          errorMessage={
            nextError
              ? cursorLost
                ? '목록이 그사이 바뀌었습니다. 처음부터 다시 조회해 주세요.'
                : '다음 항목을 불러오지 못했습니다. 이미 불러온 항목은 그대로입니다.'
              : null
          }
          restart={cursorLost ? { label: '처음부터 다시 조회', onClick: restart } : null}
          onLoadMore={() => void query.fetchNextPage()}
        />
      ) : null}
    </>,
  );
}
